import { useCallback, useEffect, useState } from 'react'
import api from '../lib/api'
import { toastError } from '../lib/toast'
import { Award, Download, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageEmpty, PageHeader, PageSpinner } from '@/components/page'

interface PendingMember {
  id: string
  name: string
  email: string
}

interface CourseSummary {
  id: string
  title: string
}

interface CertRow {
  id: string
  certificate_code: string
  course_title: string
  member_id?: string
  member_name?: string
  rendered: boolean
}

const normalizeTitle = (value: string) => value.trim().toLocaleLowerCase('es-CO')

async function downloadCertificate(code: string) {
  const res = await api.get(`/academy/certificates/${code}/download`, { responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `certificado-${code}.png`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export default function GroupCertificatesPage() {
  // Who this page is showing a certificate for — a single-select choice,
  // unrelated to progress/quiz fan-out (which now always applies to the
  // whole group roster server-side; see
  // openspec/changes/academy-group-progress-fanout).
  const [viewingMemberId, setViewingMemberId] = useState<string | null>(null)
  const [members, setMembers] = useState<PendingMember[]>([])
  const [courses, setCourses] = useState<CourseSummary[]>([])
  const [groupCerts, setGroupCerts] = useState<CertRow[]>([])
  const [memberCerts, setMemberCerts] = useState<CertRow[]>([])
  const [loading, setLoading] = useState(true)
  const [requesting, setRequesting] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [downloadingCode, setDownloadingCode] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<{ code: string; title: string } | null>(null)

  async function handleDownload(code: string, title: string) {
    setDownloadingCode(code)
    setDownloadError(null)
    try {
      await downloadCertificate(code)
    } catch {
      setDownloadError({ code, title })
    } finally {
      setDownloadingCode(null)
    }
  }

  const renderDownloadButton = (code: string, title: string) => (
    <button
      onClick={() => handleDownload(code, title)}
      disabled={downloadingCode === code}
      aria-busy={downloadingCode === code}
      aria-label={downloadingCode === code ? `Descargando certificado de ${title}` : `Descargar certificado de ${title}`}
      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg bg-primary p-2 text-primary-foreground transition-[filter,transform,opacity] hover:brightness-110 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:pointer-events-none disabled:opacity-60 sm:min-h-8 sm:min-w-8"
    >
      {downloadingCode === code
        ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        : <Download className="h-4 w-4" aria-hidden="true" />}
    </button>
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [membersRes, meRes, certsRes] = await Promise.all([
        api.get('/academy/seedbeds/group-session/members'),
        api.get('/academy/seedbeds/me'),
        api.get('/academy/certificates/group-session/list'),
      ])
      setMembers(membersRes.data.data ?? [])
      setCourses(meRes.data.data?.courses ?? [])
      setGroupCerts(certsRes.data.data?.group ?? [])
      setMemberCerts(certsRes.data.data?.members ?? [])
    } catch {
      setError('No pudimos cargar los certificados del semillero.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const activeMember = members.find((m) => m.id === viewingMemberId) ?? null

  // Excluido a propósito del patrón optimistic UI: el certificado es un
  // artefacto generado por el servidor (código + imagen renderizada) sujeto
  // a una validación de negocio que puede rechazar la solicitud (curso
  // incompleto) — no hay nada que anticipar del lado del cliente. Ver
  // openspec/changes/academy-adminpanel-optimistic-ui/design.md Decisión 3.
  async function handleRequest(courseId: string) {
    if (!activeMember) return
    setRequesting(courseId)
    try {
      await api.post(`/academy/certificates/generate-member/${courseId}`, { pending_member_id: activeMember.id })
      await load()
    } catch (err) {
      // Solo afirmamos lo que el cliente sabe: el mensaje del servidor, o el tipo de fallo por código HTTP.
      const res = (err as { response?: { status?: number; data?: { error?: string; message?: string } } }).response
      const serverMessage = res?.data?.error ?? res?.data?.message
      if (serverMessage) {
        toastError(serverMessage)
      } else if (res?.status && res.status >= 400 && res.status < 500) {
        toastError(`El servidor rechazó la solicitud para ${activeMember.name}. Verifica que haya completado el curso.`)
      } else {
        toastError('No pudimos generar el certificado. Revisa tu conexión e inténtalo de nuevo.')
      }
    } finally {
      setRequesting(null)
    }
  }

  if (loading) {
    return <PageSpinner label="Cargando certificados" />
  }

  const activeMemberCerts = memberCerts.filter((c) => c.member_id === viewingMemberId)

  return (
    <div className="space-y-10">
      <div className="space-y-4 rounded-xl border border-border bg-card p-8">
        <PageHeader
          title="Certificados del semillero"
          description="Tu certificado individual se genera a tu nombre — elige quién eres para verlo o solicitarlo."
        />
        {members.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {members.map((m) => (
              <button
                key={m.id}
                onClick={() => setViewingMemberId(m.id)}
                aria-pressed={viewingMemberId === m.id}
                className={`min-h-11 rounded-full border px-3 py-1.5 text-xs font-semibold motion-press transition-[background-color,border-color,color,transform] duration-(--dur-fast) sm:min-h-8 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 ${
                  viewingMemberId === m.id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                }`}
              >
                {m.name}
              </button>
            ))}
          </div>
        ) : (
          <PageEmpty title="Aún no hay integrantes registrados en este semillero." />
        )}
      </div>

      {error && (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-5">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" onClick={load}>Reintentar</Button>
        </div>
      )}

      {downloadError && (
        <div role="alert" className="flex flex-col items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-5">
          <p className="text-sm text-destructive">
            No pudimos descargar el certificado de {downloadError.title}. Revisa tu conexión e inténtalo de nuevo.
          </p>
          <Button variant="outline" onClick={() => handleDownload(downloadError.code, downloadError.title)}>Reintentar</Button>
        </div>
      )}

      {!error && members.length > 0 && groupCerts.length === 0 && !activeMember && (
        <p className="text-sm text-muted-foreground">
          Selecciona un integrante arriba para ver o solicitar su certificado individual.
        </p>
      )}

      {groupCerts.length > 0 && (
        <section>
          <h2 className="mb-4 text-lg font-bold text-foreground">Certificado del grupo</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {groupCerts.map((cert) => (
              <div key={cert.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Award className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{cert.course_title}</p>
                    <p className="text-xs text-muted-foreground">Certificado único del semillero</p>
                  </div>
                </div>
                {cert.rendered && renderDownloadButton(cert.certificate_code, cert.course_title)}
              </div>
            ))}
          </div>
        </section>
      )}

      {activeMember && (
        <section>
          <h2 className="mb-4 text-lg font-bold text-foreground">Certificado de {activeMember.name}</h2>
          {courses.length === 0 ? (
            <PageEmpty title="Este semillero no tiene cursos asignados todavía." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {courses.map((course) => {
                // TODO: empatar por course_id cuando /certificates devuelva el id del curso; mientras tanto, título normalizado (igual que matchCertificate en useMyCertificates).
                const existing = activeMemberCerts.find((c) => normalizeTitle(c.course_title) === normalizeTitle(course.title))
                return (
                  <div key={course.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Award className="h-5 w-5" />
                      </div>
                      <p className="font-semibold text-foreground">{course.title}</p>
                    </div>
                    {existing?.rendered ? (
                      renderDownloadButton(existing.certificate_code, `${course.title} (${activeMember.name})`)
                    ) : (
                      <button
                        onClick={() => handleRequest(course.id)}
                        disabled={requesting === course.id}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
                      >
                        {requesting === course.id && <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                        {requesting === course.id ? 'Generando...' : 'Solicitar certificado'}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}
    </div>
  )
}
